import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import currentTimeZone from '../../../../config/timeZone';
// import { useAppSelector } from '../../../redux/hooks';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
// import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import NoDataAvailable from '../../../../shared-components/no-data-available/NoDataAvailable';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
  getBucketFromRange,
} from '../../../../shared-functions/ChartFunctions';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChargingScheduleChartProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}
/**
 * Function to show custom tooltip
 * @param {any} param0 - The param0
 * @param {any} param0.active - The active prop
 * @param {any} param0.payload - The payload prop
 * @returns {any} The result
 */
function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
  const { t } = useTranslation('dashboard');
  if (active && payload && payload.length > 0) {
    const data = payload[0] as any;

    return (
      <div className="charging-schedule-custom-tooltip">
        <p className="label">{data?.payload?.label}</p>
        <div className="tooltip-values">
          <p style={{ color: '#3FA9F5' }}>
            {t('confirmed')}: {data?.payload?.confirmedCount}
          </p>
          <p style={{ color: '#F04438' }}>
            {t('canceled')}: {-(data?.payload?.canceledCount || 0)}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
CustomTooltip.defaultProps = {
  /**
   * Default props for CustomTooltip component
   */
  active: false,
  payload: [],
};

/**
 * Charging schedule chart
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @param {any} param0.filters - The filters prop
 * @returns {any} The result
 */
function ChargingScheduleChart({ startDate, endDate, filters }: ChargingScheduleChartProperties) {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  // const { chargingSchedules } = useAppSelector((state) => state.chargerStatus);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const activeRangeReference = React.useRef<string>('');
  // const [chartToggle, setChartToggle] = useState([
  //   {
  //     label: t('personal'),
  //     key: 'personal',
  //     isActive: true,
  //   },
  //   {
  //     label: t('commercial'),
  //     key: 'commercial',
  //     isActive: false,
  //   },
  // ]);
  // const [activeChargerType, setActiveChargerType] = useState<'personal' | 'commercial'>('personal');

  /**
   * Fetch power consumed data based on the selected date range
   * @param {any} rangeStartDate - The range start date
   * @param {any} rangeEndDate - The range end date
   * @param {any} hardRefresh - The hardRefresh
   */
  const fetchPowerConsumedReport = async (
    rangeStartDate: string,
    rangeEndDate: string,
    hardRefresh = false,
  ) => {
    const rangeKey = `${rangeStartDate}_${rangeEndDate}`;
    activeRangeReference.current = rangeKey;
    try {
      setIsLoading(true);
      const { facilityIds, stationIds, deviceIds, type } = filters || {};

      // If global filters are active, they take precedence or we filter by both
      // If both or none, we might want to pass both or default to current toggle
      const query: any = {
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        timeZone: currentTimeZone,
        hardRefresh,
        type,
      };
      if (facilityIds && facilityIds?.length > 0) {
        query.accountCustomId = facilityIds;
      }
      if (stationIds && stationIds?.length > 0) {
        query.stationIds = stationIds;
      }
      if (deviceIds && deviceIds?.length > 0) {
        query.deviceIds = deviceIds;
      }
      const response = await dashboard.getScheduleChargingReport(query);

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== rangeKey) return;

      // ✅ Otherwise, update the chart with the right data
      const updatedTime = response.data?.last_updated_time;
      const result = response.data?.result;
      const { bucket, days } = getBucketFromRange(rangeStartDate, rangeEndDate);
      if (response.status === 200) {
        if (bucket === 'month') {
          setBarGraphData(fillMissingMonthsGeneric(result));
        } else if (bucket === 'day') {
          setBarGraphData(fillMissingLastNDaysGeneric(result, days));
        } else {
          setBarGraphData(fillMissingLast24HrsGeneric(result));
        }
      }
      if (updatedTime) {
        setLastUpdatedTime(updatedTime);
      }
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === rangeKey) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  // /**
  //  * Function to switch the tab
  //  * @param key
  //  */
  // const handleToggle = async (
  //   key: string,
  //   range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  //   hardRefresh = false,
  // ) => {
  //   setChartToggle((previousTab) =>
  //     previousTab.map((tab) => ({ ...tab, isActive: tab.key === key })),
  //   );
  //   activeRangeReference.current = range;
  //   try {
  //     setIsLoading(true);
  //     const { facilityIds, stationIds, deviceIds, type: filterTypes } = filters || {};

  //     let requestType = key;
  //     if (filterTypes?.includes('personal') && !filterTypes?.includes('commercial')) requestType = 'personal';
  //     else if (!filterTypes?.includes('personal') && filterTypes?.includes('commercial')) requestType = 'commercial';
  //     const query: any = {
  //       timePeriod: range,
  //       timeZone: currentTimeZone,
  //       hardRefresh,
  //       type: filterTypes?.length === 2 ? filterTypes : [requestType],
  //     }
  //     if (facilityIds && facilityIds?.length > 0) {
  //       query.accountCustomerId = facilityIds;
  //     }
  //     if (stationIds && stationIds?.length > 0) {
  //       query.stationIds = stationIds;
  //     }
  //     if (deviceIds && deviceIds?.length > 0) {
  //       query.deviceIds = deviceIds;
  //     }
  //     const response = await dashboard.getScheduleChargingReport(query);

  //     if (activeRangeReference.current !== range) return;

  //     const updatedTime = response.data?.last_updated_time;
  //     const result = response.data?.result;

  //     if (range === 'last12months' && response.status === 200) {
  //       setBarGraphData(fillMissingMonthsGeneric(result));
  //     } else if (range === 'lastMonth' && response.status === 200) {
  //       setBarGraphData(fillMissingLastNDaysGeneric(result, 30));
  //     } else if (range === 'last7days' && response.status === 200) {
  //       setBarGraphData(fillMissingLastNDaysGeneric(result, 7));
  //     } else if (range === 'last24hours' && response.status === 200) {
  //       setBarGraphData(fillMissingLast24HrsGeneric(result));
  //     }

  //     if (updatedTime) setLastUpdatedTime(updatedTime);
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.log(error);
  //     if (activeRangeReference.current === range) {
  //       setIsLoading(false);
  //     }
  //   }
  // };

  useEffect(() => {
    // const activeType = chartToggle.find((iteam) => iteam.isActive)?.key as 'personal' | 'commercial';
    // setActiveChargerType(activeType);
    fetchPowerConsumedReport(startDate, endDate);
  }, [startDate, endDate, filters]);

  // useEffect(() => {
  //   setChartToggle((previousTab) =>
  //     previousTab.map((tab) => ({ ...tab, isActive: tab.key === chargingSchedules })),
  //   );

  //   if (chargingSchedules) {
  //     setActiveChargerType(chargingSchedules as 'personal' | 'commercial');
  //     handleToggle(chargingSchedules, dateDuration);
  //   }
  // }, [chargingSchedules]);

  return (
    <div className="charging-schedule-chart-wrap" dir={i18n.dir()}>
      <div className="charging-schedule-chart-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>{t('chargingSchedules')}</h6>
            <TooltipIcon text="Charging schedule chart" infoColor="info" />
          </div>
          <NavigateArrow
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/ev-admin/schedule-management/list')}
          />
          <div className="refresh">
            <div className="refresh-icon">
              {isRefreshing ? (
                <div className="loading">
                  <LoaderIcon style={{ width: '20px', height: '20px' }} />
                </div>
              ) : (
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={() => {
                    setIsRefreshing(true);
                    fetchPowerConsumedReport(
                      startDate,
                      endDate,
                      true, // hard refresh on button click
                    );
                  }}
                  svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
                />
              )}
            </div>
            <p className="updated-time">
              {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}
            </p>
          </div>
        </div>
      </div>
      {isLoading ? (
        <center className="charging-schedule-loader">
          <BatteryLoader />
        </center>
      ) : (
        <>
          {barGraphData.length === 0 ||
          barGraphData.every((item) => Object.keys(item).length <= 1) ? (
            <NoDataAvailable />
          ) : (
            <div className="charging-schedule-chart-container" style={{ height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barGraphData.map((eachData) => ({
                    ...eachData,
                    canceledCount: Number(eachData.canceledCount) > 0 ? -eachData.canceledCount : 0,
                  }))}
                  barCategoryGap={25}>
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    stroke="#535353"
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  {/* <YAxis
                axisLine={false}
                tickLine={false}
                domain={['dataMin - 1', 'dataMax + 1']}
              /> */}

                  <Bar
                    dataKey="canceledCount"
                    // stackId="a"
                    barSize={26}
                    radius={[20, 20, 0, 0]}
                    fill="#F04438"
                  />
                  <Bar
                    dataKey="confirmedCount"
                    // stackId="a"
                    barSize={26}
                    radius={[20, 20, 0, 0]}
                    fill="#3FA9F5"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="charging-schedule-chart-label">
            <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#3FA9F5' }} />
              <p>{t('confirmed')}</p>
            </div>
            <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#F04438' }} />
              <p>{t('canceled')}</p>
            </div>
          </div>
        </>
      )}
      {/* <div className="schedule-toggle">
        <ToggleWithIcons
          leftMenu={{ ...chartToggle[0], label: t('personal') }}
          rightMenu={{ ...chartToggle[1], label: t('commercial') }}
          handleClickToggle={(key: string) => {
            setActiveChargerType(key as 'personal' | 'commercial');
            handleToggle(key, dateDuration);
          }}
        />
      </div> */}
    </div>
  );
}

ChargingScheduleChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default ChargingScheduleChart;
